<script>
    import PageHeader from '$components/PageHeader.svelte';
    import SEO from '$components/SEO.svelte';
    import { applyAction, deserialize, enhance } from '$app/forms';
    import { fade } from 'svelte/transition';
    import toast from 'svelte-french-toast';
    import Spinner from '$components/utility/Spinner.svelte';
    import { goto } from '$app/navigation';
    import TextInput from '$components/TextInput.svelte';

    let otpForm;
    let showOTPInput = false;
    let email = '';
    let otp = '';
    let error = null;
    let submittingEmail = false;
    let submittingOTP = false;

    const handleSubmit = () => {
        submittingEmail = true;
        return async ({ result: { data, status }, update }) => {
            submittingEmail = false;
            if (status !== 200 || !data?.success) {
                error = 'Invalid email. Please try again.';
            } else if (status === 200 && data?.success) {
                showOTPInput = true;
                toast.success('Email sent! Check your inbox for a 6-digit code.', {
                    position: 'top-center',
                    duration: 5000
                });
            }
        };
    };

    const submitHandler = async () => {
        submittingOTP = true;

        const response = await fetch(otpForm.action, {
            method: 'POST',
            body: new FormData(otpForm)
        });
        /** @type {import('@sveltejs/kit').ActionResult} */
        const result = deserialize(await response.text());

        if (result.type === 'success') {
            // rerun all `load` functions, following the successful update
            const { data, status } = result;
            submittingOTP = false;

            if (status !== 200 || !data?.success) {
                error = 'Invalid code. Please try again.';
            } else if (status === 200 && data?.success) {
                if (data?.isFirstTime) {
                    toast.success('Sign up successful! Welcome to CondoGenius!', {
                        position: 'top-center',
                        duration: 2000
                    });
                } else {
                    toast.success('Sign in successful! Welcome back!', {
                        position: 'top-center',
                        duration: 2000
                    });
                }
                await goto('/welcome');
            }
        } else {
            submittingOTP = false;
            applyAction(result);
        }
    }

    const handleChangeEvent = async () => {
        if (isOTPValid(otp) && !submittingOTP) {
            await submitHandler();
        }
    }

    const isOTPValid = (input) => {
        return input.length === 6 && /^\d+$/.test(input);
    }
</script>

<SEO title='CondoGenius - Log In | Condo Reviews for Toronto and GTA'
     description='Log in or sign up now to write a review about your condo building and help a future tenant out!' />

<div class='w-full sm:w-1/2 mt-5 md:mt-15 flex flex-row'>
    {#if !showOTPInput}
        <div class=''>
            <PageHeader class='h2 text-3xl' text="Let's get started!" />
            <p class='mt-14'>
                First, let's sign you in.
            </p>
            <p class='pt-5'>
                <strong>Login</strong> or <strong>create a new account</strong> with your email:
            </p>
            <form action='?/signIn' method='post' use:enhance={handleSubmit}>
                <div class='flex flex-col md:flex-row gap-2 mt-10 w-full'>
                    <TextInput id='email' name='email' placeholder='Your email address'
                               type='email'
                               disabled={submittingEmail}
                               bind:value={email}
                               required={true}/>
                    <input type='submit' class='btn cta rounded-xl py-3 text-white cursor-pointer'
                           disabled={submittingEmail}
                           value='{submittingEmail ? "Submitting..." : "Send Sign In Link"}'>
                </div>
            </form>
            <p class='mt-10'>
                <strong>Why ask for your email?</strong>
            </p>
            <p class='mt-5'>
                All reviews on CondoGenius must come from a working email
                address. That way, others can trust our reviews are real!
            </p>
            <div class='mt-5'>
                Don’t worry – you always get to decide how your name is displayed in the review.
            </div>
        </div>
    {:else}
        <div class='min-w-full' in:fade>
            <PageHeader size='h2' text='Enter your 6-digit code:' />
            <p>
                We've sent a <strong>temporary 6-digit code</strong> to the email address you provided.
            </p>
            <form action='?/verify' method='post' on:submit|preventDefault={submitHandler} bind:this={otpForm}>
                <div class='flex flex-col md:flex-row gap-2 my-2 w-2/3 items-center md:w-full m-auto'>
                    <input type='hidden' id='email-hidden' name='email' bind:value={email}>
                    <input type='text' id='otp' name='otp'
                           placeholder='000000'
                           maxlength='6'
                           bind:value={otp}
                           on:keyup={handleChangeEvent}
                           class='bg-neutral-100 rounded-xl py-3 px-5 text-neutral-700 tracking-widest md:tracking-normal text-5xl md:text-3xl w-full text-center md:text-left md:w-[150px]'>
                    {#if submittingOTP}
                        <div class='align-middle top-3 relative'>
                            <Spinner />
                        </div>
                    {:else}
                        <input type='submit' id='sign-in'
                               class='btn rounded-xl py-3 text-primary-500 font-bold'
                               value='Sign In >'>
                    {/if}
                </div>
                {#if error}
                    <p class='text-error-500'>{error}</p>
                {/if}
            </form>
        </div>
    {/if}
    <img class='signup-img hidden sm:block mt-5'
         src='/homepage/signup.webp' alt='An aerial of North York' />
</div>

<style>
    .signup-img {
        top: -50px;
        border-radius: 20px;
        left: 60%;
        margin-left: -150px;
        margin-right: -50vw;
        max-width: 250vw;
        position: relative;
        right: 100%;
        max-height: 600px;
        object-fit: cover;
    }
</style>
